import React from 'react'
import { useTranslation } from 'react-i18next'
import { Document } from '../../types'
import { getRelativeTime } from '../../utils/time'
import { Dropdown } from '../ui/Dropdown'
import { Icon } from '../ui/Icon'
import { useAppStore } from '../../store/useAppStore'
import { shareDocument } from '../../services/shareService'
import { toast } from 'sonner'

/**
 * 文档项组件
 * 显示单个文档的信息
 */
interface PageItemProps {
  document: Document
  isActive: boolean
  onClick: () => void
}

export const PageItem: React.FC<PageItemProps> = ({ document, isActive, onClick }) => {
  const { t } = useTranslation()
  const { deleteDocument } = useAppStore()

  const handleShare = async () => {
    try {
      const shareInfo = await shareDocument(document)
      await navigator.clipboard.writeText(shareInfo.shareUrl)
      toast.success(t('share.success'))
    } catch (error) {
      console.error('Failed to share document:', error)
      toast.error(t('share.error'))
    }
  }

  const handleDelete = () => {
    toast.warning(t('common.confirmDelete'), {
      description: t('common.actionUndone'),
      duration: Infinity,
      action: {
        label: t('common.delete'),
        onClick: () => {
          deleteDocument(document.id)
          toast.success(t('common.deleteSuccess'))
        },
      },
      cancel: {
        label: t('common.cancel'),
        onClick: () => {},
      },
    })
  }

  return (
    <div
      className="px-4 py-2 cursor-pointer border-l-4 transition-colors group relative"
      style={{
        background: isActive ? 'var(--accent-blue-bg)' : 'transparent',
        borderLeftColor: isActive ? 'var(--accent-blue)' : 'transparent'
      }}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        {/* 文档标题 */}
        <h3 
          className="text-sm font-medium truncate flex-1"
          style={{
            color: 'var(--text-primary)'
          }}
        >
          {document.title}
        </h3>

        {/* 更多按钮 */}
        <div 
          className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            options={[
              { 
                value: 'share', 
                label: t('nav.share'), 
                icon: <Icon name="share" size={14} /> 
              },
              { 
                value: 'delete', 
                label: t('common.delete'), 
                icon: <Icon name="trash" size={14} />,
                variant: 'danger'
              }
            ]}
            value=""
            onChange={(val) => {
              if (val === 'share') handleShare()
              if (val === 'delete') handleDelete()
            }}
            triggerIcon={<Icon name="more-horizontal" size={16} />}
            hideChevron
            variant="ghost"
            className="h-6 w-6 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
          />
        </div>
      </div>
      
      {/* 文档信息 */}
      <div className="flex items-center justify-between mt-1">
        <p 
          className="text-xs truncate"
          style={{
            color: 'var(--text-secondary)'
          }}
        >
          {document.content ? document.content.slice(0, 50) + '...' : t('sidebar.emptyDocument')}
        </p>
        <span
          className="text-xs ml-2 whitespace-nowrap"
          style={{
            color: 'var(--text-tertiary)'
          }}
        >
          {getRelativeTime(document.updated)}
        </span>
      </div>
    </div>
  )
}